/**
 * PwaInstallPrompt — Phase 3 §3.3
 * Captures the `beforeinstallprompt` event and renders a discreet bottom-sheet
 * inviting the user to install the PWA on Android/Chromium desktop. iOS Safari
 * shows manual instructions (Add to Home Screen).
 *
 * The component self-hides if:
 *  - already installed (display-mode: standalone or navigator.standalone)
 *  - the user dismissed it in the last 30 days (localStorage flag)
 *  - the route is on the public landing/login (we want admins inside the app)
 */
import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Download, X, Smartphone } from 'lucide-react';

const DISMISS_KEY = 'remobiles.pwa.installPrompt.dismissedAt';
const DISMISS_TTL_MS = 30 * 24 * 3600 * 1000;

function isStandalone() {
  if (typeof window === 'undefined') return false;
  return (
    window.matchMedia?.('(display-mode: standalone)').matches ||
    window.navigator.standalone === true
  );
}

function isIOS() {
  if (typeof navigator === 'undefined') return false;
  return /iphone|ipad|ipod/i.test(navigator.userAgent) && !window.MSStream;
}

export default function PwaInstallPrompt() {
  const location = useLocation();
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [show, setShow] = useState(false);
  const [showIOSHint, setShowIOSHint] = useState(false);

  useEffect(() => {
    if (isStandalone()) return undefined;

    const dismissedAt = Number(localStorage.getItem(DISMISS_KEY) || 0);
    if (dismissedAt && Date.now() - dismissedAt < DISMISS_TTL_MS) return undefined;

    const onBeforeInstall = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setShow(true);
    };

    const onInstalled = () => {
      setShow(false);
      setDeferredPrompt(null);
      localStorage.setItem(DISMISS_KEY, String(Date.now()));
    };

    window.addEventListener('beforeinstallprompt', onBeforeInstall);
    window.addEventListener('appinstalled', onInstalled);

    // iOS fallback: show hint after 8s on internal routes only
    if (isIOS()) {
      const t = setTimeout(() => setShowIOSHint(true), 8000);
      return () => {
        clearTimeout(t);
        window.removeEventListener('beforeinstallprompt', onBeforeInstall);
        window.removeEventListener('appinstalled', onInstalled);
      };
    }

    return () => {
      window.removeEventListener('beforeinstallprompt', onBeforeInstall);
      window.removeEventListener('appinstalled', onInstalled);
    };
  }, []);

  // Don't show on landing/login (admin/staff inside app only)
  const path = location.pathname;
  const isPublic = path === '/' || path.startsWith('/login') || path.startsWith('/inscription')
    || path.startsWith('/r/') || path === '/remobiles' || path.startsWith('/track/');
  if (isPublic) return null;

  const dismiss = () => {
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
    setShow(false);
    setShowIOSHint(false);
  };

  const accept = async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    try {
      await deferredPrompt.userChoice;
    } catch { /* noop */ }
    setDeferredPrompt(null);
    setShow(false);
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
  };

  if (!show && !showIOSHint) return null;

  return (
    <div className="pwa-toast" role="region" aria-label="Installer Remobiles" data-testid="pwa-install-prompt">
      <div className="pwa-toast-icon">
        {showIOSHint ? <Smartphone size={20} /> : <Download size={20} />}
      </div>
      <div className="pwa-toast-body">
        <div className="pwa-toast-title">Installer Remobiles</div>
        <div className="pwa-toast-sub">
          {showIOSHint
            ? 'Sur iPhone : appuyez sur Partager puis « Sur l’écran d’accueil ».'
            : 'Accès rapide depuis votre bureau / écran d’accueil.'}
        </div>
      </div>
      {!showIOSHint && (
        <button type="button" className="pwa-toast-cta" onClick={accept} data-testid="pwa-install-accept">
          Installer
        </button>
      )}
      <button type="button" className="pwa-toast-close" onClick={dismiss} aria-label="Fermer" data-testid="pwa-install-dismiss">
        <X size={14} />
      </button>

      <style>{`
        .pwa-toast {
          position: fixed;
          left: 50%;
          bottom: 24px;
          transform: translateX(-50%);
          z-index: 9980;
          display: flex; align-items: center; gap: 12px;
          background: linear-gradient(155deg, #141414 0%, #0a0a0a 100%);
          color: #f5f5f5;
          border: 1px solid rgba(212,175,55,0.32);
          border-radius: 14px;
          padding: 12px 14px;
          box-shadow: 0 18px 40px rgba(0,0,0,0.55), 0 0 0 1px rgba(0,0,0,0.4);
          font-family: -apple-system, "SF Pro Display", "Segoe UI", system-ui, sans-serif;
          max-width: 92vw;
          animation: pwaSlide .25s cubic-bezier(.2,.9,.3,1.2);
        }
        @keyframes pwaSlide { from { transform: translate(-50%, 16px); opacity: 0 } to { transform: translateX(-50%); opacity: 1 } }
        .pwa-toast-icon {
          width: 36px; height: 36px;
          border-radius: 10px;
          background: rgba(200,16,46,0.16);
          color: #d4af37;
          display: grid; place-items: center;
          border: 1px solid rgba(212,175,55,0.3);
          flex-shrink: 0;
        }
        .pwa-toast-body { display: flex; flex-direction: column; }
        .pwa-toast-title { font-weight: 700; font-size: 14px; }
        .pwa-toast-sub { font-size: 12px; color: #aaa; max-width: 280px; }
        .pwa-toast-cta {
          background: linear-gradient(145deg, #d11833 0%, #8a0a1d 100%);
          color: #fff;
          border: 1px solid #d4af37;
          padding: 8px 14px;
          border-radius: 999px;
          font-size: 13px; font-weight: 600;
          cursor: pointer;
          transition: transform .15s ease;
        }
        .pwa-toast-cta:hover { transform: translateY(-1px); }
        .pwa-toast-close {
          background: transparent; border: none; color: #888;
          cursor: pointer; padding: 4px; line-height: 0;
        }
        .pwa-toast-close:hover { color: #fff; }
        @media (max-width: 480px) {
          .pwa-toast-sub { max-width: 180px; }
        }
      `}</style>
    </div>
  );
}
